import React from 'react';
import { Link } from 'react-router-dom';
import { Factory, ClipboardCheck, BarChart3, CircleDollarSign, ArrowRight, CheckCircle } from 'lucide-react';

const Services = () => {
  // SEO handled by centralized SEO component in App.js
  const services = [
    {
      icon: <Factory className="w-8 h-8" />,
      title: 'Air Permit Applications & Strategy',
      category: 'Air Quality Permitting',
      description: 'Expert preparation and strategic planning for all air quality permits in Alaska.',
      features: [
        'Expert preparation of Title V, Minor Source, and Synthetic Minor permit applications',
        'Strategic permitting to avoid unnecessary regulatory classifications',
        'General Permit (MG1, MG2) assistance for portable oil and gas operations',
        'General Permit (MG3, MG9) support for rock crushing and asphalt plants',
        'Negotiation of permit terms with Alaska DEC to ensure operational flexibility'
      ],
      link: '/services/permit-applications',
      gradient: 'from-emerald-500 to-teal-600',
      iconBg: 'bg-emerald-600',
      textColor: 'text-emerald-700',
      hoverBorder: 'hover:border-emerald-500'
    },
    {
      icon: <ClipboardCheck className="w-8 h-8" />,
      title: 'Environmental Compliance Management',
      category: 'Compliance & Reporting',
      description: 'Comprehensive compliance tracking and reporting solutions to prevent violations.',
      features: [
        'Comprehensive emissions inventory development and Triennial Reporting',
        'Greenhouse Gas (GHG) mandatory reporting (40 CFR Part 98)',
        'Development of Facility Operating Reports (FOR) and Excess Emissions Reports (EER)',
        'Compliance tracking systems to prevent violations and fines'
      ],
      link: '/services/compliance-support',
      gradient: 'from-slate-500 to-blue-600',
      iconBg: 'bg-blue-600',
      textColor: 'text-slate-700',
      hoverBorder: 'hover:border-slate-500'
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'Modeling & Technical Support',
      category: 'Technical Support',
      description: 'Advanced technical expertise for complex environmental assessments.',
      features: [
        'Dispersion modeling oversight (AERMOD) using Interior Alaska and Arctic meteorological data',
        'Stack testing consulting and coordination with testing vendors',
        'Fugitive dust control plans and opacity monitoring support',
        'NEPA environmental assessments for federal projects'
      ],
      link: '/modeling-technical-support',
      gradient: 'from-amber-500 to-yellow-600',
      iconBg: 'bg-amber-500',
      textColor: 'text-amber-700',
      hoverBorder: 'hover:border-amber-500'
    },
    {
      icon: <CircleDollarSign className="w-8 h-8" />,
      title: 'Capital-Efficient Project Planning',
      category: 'Feasibility & Capital Efficiency',
      description: 'Strategic planning to minimize costs and maximize operational flexibility.',
      features: [
        'Pre-construction permit feasibility analysis to identify "showstopper" regulations',
        'Equipment selection guidance to minimize pollution control costs',
        'Strategies to reduce capital cost impacts by 30–40% through right-sized permitting'
      ],
      link: '/services/cost-effective-solutions',
      gradient: 'from-violet-500 to-purple-600',
      iconBg: 'bg-violet-600',
      textColor: 'text-violet-700',
      hoverBorder: 'hover:border-violet-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative text-white py-20 md:py-32 overflow-hidden">
        {/* Responsive Background Image */}
        <picture className="absolute inset-0 w-full h-full">
          {/* Desktop Image (>1024px) */}
          <source
            media="(min-width: 1025px)"
            srcSet="/images/raven_juneau_1920x1080.webp"
          />
          {/* Tablet Image (768px-1024px) */}
          <source
            media="(min-width: 768px) and (max-width: 1024px)"
            srcSet="/images/raven_juneau_1536x864.webp"
          />
          {/* Mobile Image (<768px) - Vertical */}
          <source
            media="(max-width: 767px)"
            srcSet="/images/raven_juneau_1080x1920.webp"
          />
          {/* Fallback */}
          <img
            src="/images/raven_juneau_1920x1080.webp"
            alt="Alaska industrial operations with mountains in background - Raven Environmental Services"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </picture>

        {/* Dark Overlay for Text Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-2xl" style={{ textShadow: '2px 2px 8px rgba(0, 0, 0, 0.9)' }}>
              Comprehensive Air Quality & Environmental Services
            </h1>
            <p className="text-xl md:text-2xl mb-6 text-gray-50 leading-relaxed drop-shadow-lg" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              Expert permitting, compliance strategies, and technical support tailored to Alaska's industrial and remote operations.
            </p>
            <p className="text-lg md:text-xl mb-8 text-emerald-300 font-medium leading-relaxed drop-shadow-lg" style={{ textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)' }}>
              From Title V permits to dispersion modeling and capital-efficient project planning—we deliver strategic compliance solutions that protect your operations and bottom line.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              Get Started Today
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Strategic Compliance for Alaska's Unique Challenges
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Navigating Alaska's regulatory environment requires more than just technical knowledge; it requires local strategy. We help industrial facilities, utilities, and military installations turn complex EPA and DEC regulations into clear, manageable operations. We provide certainty in an uncertain regulatory landscape.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Raven Environmental Consulting provides specialized technical support for Alaska's regulatory landscape. From Title V air permits to emissions inventory management, dispersion modeling using Arctic meteorological data, to capital-efficient project planning—we deliver comprehensive solutions that protect your operations while minimizing costs and regulatory burden.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid - 2x2 Layout with Enhanced Visual Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className={`group bg-white border border-gray-200 ${service.hoverBorder} rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300`}
              >
                {/* Gradient Header with Jewel Icon */}
                <div className={`bg-gradient-to-r ${service.gradient} p-6`}>
                  <div className="flex items-center space-x-4">
                    <div className={`w-16 h-16 ${service.iconBg} rounded-full flex items-center justify-center shadow-lg`}>
                      <div className="text-white">
                        {service.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-8">
                  {/* Service Description */}
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className={`w-5 h-5 ${service.textColor} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700 leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <Link
                    to={service.link}
                    className={`inline-flex items-center ${service.textColor} font-semibold hover:opacity-80 transition-opacity group-hover:translate-x-1 transform transition-transform duration-200`}
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-900 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need a specific permit or compliance solution?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Contact us to discuss your project and discover how we can help you achieve compliance efficiently.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            Contact Us
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
